import { useEffect, useRef, useState } from "react";

import type { TokenDef } from "../data/tokens.js";
import type { WalletState } from "../lib/wallet.js";

import { readTokenBalance } from "../lib/balance.js";
import { formatTokenAmount } from "../lib/format.js";
import { getInjectedProvider } from "../lib/wallet.js";
import styles from "../styles/BalancePill.module.css";

/**
 * Small chip rendered under each token picker when the wallet is
 * connected. Shows the user's balance of `token` on `chainId` via a
 * single `balanceOf` (or `getBalance` for native gas).
 *
 * Behavior:
 *   - Fetches on mount + whenever `(chainId, token, wallet)` change.
 *   - Cancels stale requests via a `seq` counter; the wallet may flip
 *     chains underneath the SPA, so a slow RPC reply from chain A
 *     mustn't overwrite a fresher result from chain B.
 *   - Failures (rate limit, RPC down) surface inline as "—" — no
 *     toast; this is informational and the form still submits fine.
 *
 * Performance: each pill issues one read on render. With 2 pills
 * (`fromToken`, `toToken`) and the average switch frequency the user
 * cycles tokens at, this is negligible. If we ever render many at
 * once, a shared multicall reader would be the next step.
 */
export interface BalancePillProps {
  readonly chainId: number;
  readonly token: TokenDef;
  readonly wallet: WalletState | undefined;
  /**
   * Bump counter from the parent: every increment forces this pill to
   * refetch the balance (e.g. once the StepsExecutor confirms all steps
   * on-chain, the parent bumps the counter so we re-read the now-updated
   * balanceOf without remounting the form).
   */
  readonly refreshKey?: number;
}

type State =
  | { readonly kind: "idle" }
  | { readonly kind: "loading" }
  | { readonly kind: "ok"; readonly raw: bigint }
  | { readonly kind: "error" };

export function BalancePill({
  chainId,
  token,
  wallet,
  refreshKey,
}: BalancePillProps): React.JSX.Element | null {
  const [state, setState] = useState<State>({ kind: "idle" });
  const seqRef = useRef(0);

  useEffect(() => {
    // The state writes here are the documented "subscribe to external
    // system" pattern (RPC balance lookup). The eslint rule flags them
    // as cascading-render risk, but each is the consequence of a fresh
    // `(chainId, token, wallet)` change — not a render-driven loop.
    /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */
    if (wallet === undefined) {
      setState({ kind: "idle" });
      return;
    }
    const provider = getInjectedProvider();
    if (provider === undefined) {
      setState({ kind: "idle" });
      return;
    }

    const seq = ++seqRef.current;
    setState({ kind: "loading" });
    /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

    readTokenBalance({
      provider,
      chainId,
      token: token.address,
      owner: wallet.address,
      isNative: token.isNative,
    })
      .then((raw) => {
        if (seq !== seqRef.current) return;
        setState({ kind: "ok", raw });
      })
      .catch(() => {
        if (seq !== seqRef.current) return;
        setState({ kind: "error" });
      });
    // refreshKey is intentionally in the dep list: bumping it from the
    // parent (e.g. on `onCompleted`) is the only mechanism we have for
    // an external "refetch now" trigger without exposing an imperative
    // handle from the pill.
  }, [chainId, token.address, token.isNative, wallet, refreshKey]);

  if (wallet === undefined) {
    return (
      <span className={styles.pillMuted}>Conecta wallet para ver saldo</span>
    );
  }

  if (state.kind === "loading") {
    return <span className={styles.pillMuted}>Saldo: …</span>;
  }
  if (state.kind === "error") {
    return <span className={styles.pillMuted}>Saldo: no disponible</span>;
  }
  if (state.kind === "ok") {
    // formatTokenAmount already appends the symbol (e.g. "0.413461 USDT").
    return (
      <span className={styles.pill}>
        Saldo: {formatTokenAmount(state.raw.toString(), token)}
      </span>
    );
  }
  return null;
}
